import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUp, Users, Star, MapPin, Clock, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
const EmpleoPage = () => {
  const {
    toast
  } = useToast();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    posicion: '',
    experiencia: '',
    mensaje: '',
    cv: null as File | null
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "CV Enviado",
      description: "Hemos recibido tu aplicación. Te contactaremos pronto."
    });
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      posicion: '',
      experiencia: '',
      mensaje: '',
      cv: null
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({
        ...formData,
        cv: file
      });
    }
  };
  const posicionesDisponibles = [{
    titulo: "Técnico en Telecomunicaciones",
    ubicacion: "Buenos Aires",
    tipo: "Tiempo Completo",
    descripcion: "Buscamos técnico especializado en instalación y mantenimiento de infraestructura de fibra óptica."
  }, {
    titulo: "Supervisor de Obras",
    ubicacion: "AMBA",
    tipo: "Tiempo Completo",
    descripcion: "Supervisión de proyectos de canalización y instalación de redes de telecomunicaciones."
  }, {
    titulo: "Operario de Construcción",
    ubicacion: "Buenos Aires",
    tipo: "Tiempo Completo",
    descripcion: "Participación en obras de excavación, instalación de ductos y trabajos de infraestructura."
  }];
  const beneficios = [{
    icon: Users,
    titulo: "Equipo Profesional",
    descripcion: "Trabaja con profesionales experimentados en telecomunicaciones"
  }, {
    icon: Star,
    titulo: "Capacitación Continua",
    descripcion: "Programas de formación y actualización técnica permanente"
  }, {
    icon: Shield,
    titulo: "Seguridad Laboral",
    descripcion: "Cumplimiento estricto de normas de seguridad e higiene"
  }, {
    icon: MapPin,
    titulo: "Proyectos Diversos",
    descripcion: "Participación en proyectos de infraestructura de gran escala"
  }];
  return <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-hero text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Únete a Nuestro Equipo
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">Construye tu carrera en la empresa líder de infraestructura de telecomunicaciones</p>
          <Button variant="outline" size="xl" className="border-white text-white bg-white/10 hover:bg-white hover:text-enausa-navy backdrop-blur-sm" onClick={() => document.getElementById('formulario-cv')?.scrollIntoView({
          behavior: 'smooth'
        })}>
            <FileUp className="w-5 h-5 mr-2" />
            Enviar CV
          </Button>
        </div>
      </section>

      {/* Por qué trabajar con nosotros */}
      <section className="py-20 bg-enausa-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-enausa-navy mb-6">
              ¿Por qué trabajar en ENAUSA?
            </h2>
            <p className="text-xl text-enausa-gray max-w-3xl mx-auto">
              Somos una empresa en crecimiento que valora el talento y ofrece oportunidades 
              de desarrollo profesional en el sector de telecomunicaciones.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {beneficios.map((beneficio, index) => <Card key={index} className="text-center shadow-medium hover:shadow-strong transition-all duration-300">
                <CardHeader>
                  <div className="w-16 h-16 bg-enausa-navy rounded-full flex items-center justify-center mx-auto mb-4">
                    <beneficio.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-lg text-enausa-navy">{beneficio.titulo}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-enausa-gray">{beneficio.descripcion}</CardDescription>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Posiciones Disponibles */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-enausa-navy mb-6">
              Posiciones Disponibles
            </h2>
            <p className="text-xl text-enausa-gray max-w-3xl mx-auto">
              Explora las oportunidades laborales actuales en nuestro equipo de trabajo.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {posicionesDisponibles.map((posicion, index) => <Card key={index} className="shadow-medium hover:shadow-strong transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-xl text-enausa-navy">{posicion.titulo}</CardTitle>
                  <div className="flex items-center justify-between text-sm text-enausa-gray">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {posicion.ubicacion}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {posicion.tipo}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-enausa-gray mb-4">{posicion.descripcion}</p>
                  <Button variant="enausa-outline" size="sm" className="w-full" onClick={() => document.getElementById('formulario-cv')?.scrollIntoView({
                behavior: 'smooth'
              })}>
                    Aplicar a esta Posición
                  </Button>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Formulario de Aplicación */}
      <section id="formulario-cv" className="py-20 bg-enausa-light">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-enausa-navy mb-6">
                Envía tu CV
              </h2>
              <p className="text-xl text-enausa-gray">
                Completa el formulario y adjunta tu currículum. Nos pondremos en contacto contigo.
              </p>
            </div>

            <Card className="shadow-strong">
              <CardHeader>
                <CardTitle className="text-2xl text-enausa-navy text-center">
                  Formulario de Aplicación
                </CardTitle>
                <CardDescription className="text-center">
                  Todos los campos marcados con * son obligatorios
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="nombre">Nombre Completo *</Label>
                      <Input id="nombre" type="text" required value={formData.nombre} onChange={e => setFormData({
                      ...formData,
                      nombre: e.target.value
                    })} placeholder="Tu nombre completo" />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" required value={formData.email} onChange={e => setFormData({
                      ...formData,
                      email: e.target.value
                    })} placeholder="tu@email.com" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="telefono">Teléfono *</Label>
                      <Input id="telefono" type="tel" required value={formData.telefono} onChange={e => setFormData({
                      ...formData,
                      telefono: e.target.value
                    })} placeholder="+54 11 xxxx-xxxx" />
                    </div>
                    <div>
                      <Label htmlFor="posicion">Posición de Interés *</Label>
                      <Select value={formData.posicion} onValueChange={value => setFormData({
                      ...formData,
                      posicion: value
                    })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona una posición" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tecnico">Técnico en Telecomunicaciones</SelectItem>
                          <SelectItem value="supervisor">Supervisor de Obras</SelectItem>
                          <SelectItem value="operario">Operario de Construcción</SelectItem>
                          <SelectItem value="otro">Otra posición</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="experiencia">Años de Experiencia</Label>
                    <Select value={formData.experiencia} onValueChange={value => setFormData({
                    ...formData,
                    experiencia: value
                  })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona tu experiencia" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sin-experiencia">Sin experiencia</SelectItem>
                        <SelectItem value="1-2">1-2 años</SelectItem>
                        <SelectItem value="3-5">3-5 años</SelectItem>
                        <SelectItem value="5+">Más de 5 años</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="cv">Adjuntar CV *</Label>
                    <div className="mt-2">
                      <Input id="cv" type="file" required accept=".pdf,.doc,.docx" onChange={handleFileChange} className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-enausa-navy file:text-white hover:file:bg-enausa-navy-light" />
                      <p className="text-sm text-enausa-gray mt-2">
                        Formatos aceptados: PDF, DOC, DOCX (máximo 5MB)
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="mensaje">Mensaje Adicional</Label>
                    <Textarea id="mensaje" value={formData.mensaje} onChange={e => setFormData({
                    ...formData,
                    mensaje: e.target.value
                  })} placeholder="Cuéntanos por qué te interesa trabajar en ENAUSA..." className="min-h-[120px]" />
                  </div>

                  <div className="text-center">
                    <Button type="submit" variant="enausa" size="lg" className="w-full md:w-auto">
                      <FileUp className="w-5 h-5 mr-2" />
                      Enviar Aplicación
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
};
export default EmpleoPage;